// @flow
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import Dropzone from 'react-dropzone';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { i18n } from 'shared/utils';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Button from 'shared/components/common/Button';
import Loader from 'shared/components/common/Loader';
import Icon from 'shared/components/common/Icon';
import UploadCSVImport from 'main-app/mutations/UploadCSVImport';
import ValidateCSVImport from 'main-app/mutations/ValidateCSVImport';
import ExecuteCSVImport from 'main-app/mutations/ExecuteCSVImport';
import ColumnMapper from './ColumnMapper';

const ENTITY_OPTIONS = [
  { value: 'ITEM', label: 'Items (Inventory)' },
  { value: 'CUSTOMER', label: 'Customers' },
  { value: 'VENDOR', label: 'Vendors' },
  { value: 'ORDER', label: 'Orders (Sales Orders)' },
];

type Props = { history: Object };

type State = {
  step: 'select' | 'upload' | 'mapping' | 'validating' | 'validated' | 'importing' | 'complete',
  entityType: ?string,
  csvImport: ?Object,
  validationResult: ?Object,
  error: ?string,
  fieldDefinitions: Array<Object>,
  columnMapping: Object,
};

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 24px;
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const Step = styled.div`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  background: ${p => (p.active ? '#1890ff' : p.done ? '#52c41a' : '#f0f0f0')};
  color: ${p => (p.active || p.done ? '#fff' : '#999')};
`;

const EntityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin: 24px 0;
`;

const EntityCard = styled.div`
  padding: 24px;
  border: 2px solid ${p => (p.selected ? '#1890ff' : '#e8e8e8')};
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: ${p => (p.selected ? '#e6f7ff' : '#fff')};
  &:hover {
    border-color: #1890ff;
  }
`;

const EntityTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EntityDesc = styled.div`
  font-size: 13px;
  color: #666;
`;

const DropzoneArea = styled.div`
  border: 2px dashed ${p => (p.active ? '#1890ff' : '#d9d9d9')};
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  background: ${p => (p.active ? '#e6f7ff' : '#fafafa')};
  margin: 24px 0;
  transition: all 0.2s;
`;

const ErrorBox = styled.div`
  background: #fff2f0;
  border: 1px solid #ffa39e;
  border-radius: 4px;
  padding: 12px 16px;
  margin: 16px 0;
  color: #a8071a;
`;

const SuccessBox = styled.div`
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  padding: 24px;
  margin: 24px 0;
  text-align: center;
`;

const SuccessTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #52c41a;
  margin-bottom: 8px;
`;

const SuccessStats = styled.div`
  font-size: 14px;
  color: #333;
  margin: 8px 0;
`;

const SkippedTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  font-size: 13px;

  th, td {
    border: 1px solid #e8e8e8;
    padding: 8px 12px;
    text-align: left;
  }
  th {
    background: #fafafa;
    font-weight: 600;
  }
  tr:nth-child(even) td {
    background: #fafafa;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
`;

const BackLink = styled.span`
  color: #1890ff;
  cursor: pointer;
  font-size: 14px;
  &:hover { text-decoration: underline; }
`;

const PreviousImportsList = styled.div`
  margin-top: 40px;
`;

const ImportRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => {
    switch (p.status) {
      case 'COMPLETED': return '#f6ffed';
      case 'FAILED': return '#fff2f0';
      case 'IMPORTING': return '#e6f7ff';
      default: return '#f0f0f0';
    }
  }};
  color: ${p => {
    switch (p.status) {
      case 'COMPLETED': return '#52c41a';
      case 'FAILED': return '#f5222d';
      case 'IMPORTING': return '#1890ff';
      default: return '#666';
    }
  }};
`;

const ENTITY_DESCRIPTIONS = {
  ITEM: 'Import inventory items with name, SKU, prices, quantities, UOM',
  CUSTOMER: 'Import customer records with contact info and addresses',
  VENDOR: 'Import vendor/supplier records with contact details',
  ORDER: 'Import sales order headers with customer, status, dates, and totals',
};

class CSVImportScene extends PureComponent<Props, State> {
  state = {
    step: 'select',
    entityType: null,
    csvImport: null,
    validationResult: null,
    error: null,
    fieldDefinitions: [],
    columnMapping: {},
  };

  handleSelectEntity = (entityType: string) => {
    this.setState({ entityType, step: 'upload', error: null });
  };

  handleFileDrop = async (accepted: Array<Object>) => {
    if (!accepted.length) return;
    const file = accepted[0];
    const { entityType } = this.state;
    if (!entityType) return;

    this.setState({ error: null });

    try {
      const result = await UploadCSVImport({
        variables: { input: { entityType } },
        uploadables: { file },
      });

      const csvImport = result.uploadCSVImport.csvImport;
      this.setState({
        csvImport,
        columnMapping: csvImport.columnMapping || {},
        step: 'mapping',
      });

      // Fetch field definitions
      this.fetchFieldDefinitions(entityType);
    } catch (err) {
      this.setState({ error: err.message || 'Upload failed' });
    }
  };

  fetchFieldDefinitions = (entityType: string) => {
    // Field definitions are fetched via QueryRenderer in ColumnMapper
    // We just pass entityType to it
  };

  handleMappingChange = (mapping: Object) => {
    this.setState({ columnMapping: mapping });
  };

  handleValidate = async () => {
    const { csvImport, columnMapping } = this.state;
    if (!csvImport) return;

    this.setState({ step: 'validating', error: null });

    try {
      const result = await ValidateCSVImport({
        variables: {
          input: {
            csvImportId: csvImport.csvImportId,
            columnMapping,
          },
        },
      });

      const validationResult = result.validateCSVImport.validationResult;
      this.setState({
        validationResult,
        step: 'validated',
      });
    } catch (err) {
      this.setState({ error: err.message, step: 'mapping' });
    }
  };

  handleExecute = async () => {
    const { csvImport } = this.state;
    if (!csvImport) return;

    this.setState({ step: 'importing', error: null });

    try {
      const result = await ExecuteCSVImport({
        variables: { input: { csvImportId: csvImport.csvImportId } },
      });

      this.setState({
        csvImport: result.executeCSVImport.csvImport,
        step: 'complete',
      });
    } catch (err) {
      this.setState({ error: err.message, step: 'validated' });
    }
  };

  handleReset = () => {
    this.setState({
      step: 'select',
      entityType: null,
      csvImport: null,
      validationResult: null,
      error: null,
      fieldDefinitions: [],
      columnMapping: {},
    });
  };

  getStepNumber = () => {
    const { step } = this.state;
    switch (step) {
      case 'select': return 0;
      case 'upload': return 1;
      case 'mapping': return 2;
      case 'validating':
      case 'validated': return 3;
      case 'importing':
      case 'complete': return 4;
      default: return 0;
    }
  };

  renderStepIndicator() {
    const current = this.getStepNumber();
    const steps = ['Select Type', 'Upload CSV', 'Map Columns', 'Validate', 'Import'];
    return (
      <StepIndicator>
        {steps.map((label, i) => (
          <Step key={label} active={i === current} done={i < current}>
            {i + 1}. {label}
          </Step>
        ))}
      </StepIndicator>
    );
  }

  renderSelectStep() {
    const { entityType } = this.state;
    return (
      <div>
        <h3>{i18n.t('What would you like to import?')}</h3>
        <EntityGrid>
          {ENTITY_OPTIONS.map(opt => (
            <EntityCard
              key={opt.value}
              selected={entityType === opt.value}
              onClick={() => this.handleSelectEntity(opt.value)}
            >
              <EntityTitle>{opt.label}</EntityTitle>
              <EntityDesc>{ENTITY_DESCRIPTIONS[opt.value]}</EntityDesc>
            </EntityCard>
          ))}
        </EntityGrid>
      </div>
    );
  }

  renderUploadStep() {
    const { entityType } = this.state;
    const label = ENTITY_OPTIONS.find(o => o.value === entityType);
    return (
      <div>
        <h3>{i18n.t(`Upload CSV — ${label ? label.label : ''}`)}</h3>
        <p style={{ color: '#666', fontSize: 14 }}>
          {i18n.t('Upload a UTF-8 CSV file with headers. Maximum 5,000 rows / 10MB.')}
        </p>
        <Dropzone
          onDrop={this.handleFileDrop}
          accept=".csv"
          maxSize={10 * 1024 * 1024}
          multiple={false}
        >
          {({ getRootProps, getInputProps, isDragActive }) => (
            <DropzoneArea {...getRootProps()} active={isDragActive}>
              <input {...getInputProps()} />
              <Icon type="circle-arrow-up" size={40} />
              <p style={{ marginTop: 12, fontSize: 16 }}>
                {i18n.t('Drag & drop your CSV file here, or click to browse')}
              </p>
              <p style={{ color: '#999', fontSize: 13 }}>
                {i18n.t('CSV files only (.csv)')}
              </p>
            </DropzoneArea>
          )}
        </Dropzone>
        <Footer>
          <BackLink onClick={this.handleReset}>{i18n.t('← Back to type selection')}</BackLink>
          <div />
        </Footer>
      </div>
    );
  }

  renderMappingStep() {
    const { csvImport, columnMapping, entityType } = this.state;
    if (!csvImport || !entityType) return null;

    return (
      <div>
        <h3>{i18n.t('Map CSV Columns')}</h3>
        <p style={{ color: '#666', fontSize: 14 }}>
          {i18n.t(`${csvImport.totalRows} rows detected in "${csvImport.fileName}". Map your CSV columns to system fields.`)}
        </p>

        <QueryRenderer
          environment={relayEnvironment}
          query={graphql`
            query CSVImportSceneFieldsQuery($entityType: CSVImportEntity!) {
              csvFieldDefinitions(entityType: $entityType) {
                key
                label
                required
              }
            }
          `}
          variables={{ entityType }}
          render={({ props: qProps }) => {
            if (!qProps) return <Loader />;
            return (
              <ColumnMapper
                headers={csvImport.headers}
                previewRows={csvImport.previewRows}
                fieldDefinitions={qProps.csvFieldDefinitions}
                mapping={columnMapping}
                onMappingChange={this.handleMappingChange}
              />
            );
          }}
        />

        <Footer>
          <BackLink onClick={() => this.setState({ step: 'upload' })}>
            {i18n.t('← Re-upload file')}
          </BackLink>
          <Button theme="primary" onClick={this.handleValidate}>
            {i18n.t('Validate')}
          </Button>
        </Footer>
      </div>
    );
  }

  renderValidatingStep() {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Loader />
        <p style={{ marginTop: 16 }}>{i18n.t('Validating your data...')}</p>
      </div>
    );
  }

  renderValidatedStep() {
    const { validationResult, csvImport } = this.state;
    if (!validationResult) return null;

    return (
      <div>
        <h3>{i18n.t('Validation Results')}</h3>
        {validationResult.valid ? (
          <SuccessBox>
            <SuccessTitle>{i18n.t('All rows passed validation')}</SuccessTitle>
            <p>{i18n.t(`Ready to import ${csvImport ? csvImport.totalRows : 0} records.`)}</p>
            {validationResult.updateCount > 0 && (
              <p style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                {i18n.t(`${validationResult.newCount || 0} new record(s) will be created, ${validationResult.updateCount} existing record(s) will be updated.`)}
              </p>
            )}
          </SuccessBox>
        ) : (
          <div>
            <ErrorBox>
              {i18n.t(`${validationResult.totalErrors} validation error(s) found. Fix your CSV and re-upload, or proceed to import valid rows (errors will be skipped).`)}
            </ErrorBox>
            <SkippedTable>
              <thead>
                <tr>
                  <th>{i18n.t('Row')}</th>
                  <th>{i18n.t('Issue')}</th>
                </tr>
              </thead>
              <tbody>
                {(validationResult.errors || []).slice(0, 50).map((err, idx) => (
                  <tr key={idx}>
                    <td>{err.row}</td>
                    <td>{err.reason}</td>
                  </tr>
                ))}
              </tbody>
            </SkippedTable>
            {validationResult.totalErrors > 50 && (
              <p style={{ color: '#999', fontSize: 13, marginTop: 8 }}>
                {i18n.t(`...and ${validationResult.totalErrors - 50} more errors`)}
              </p>
            )}
          </div>
        )}
        <Footer>
          <BackLink onClick={() => this.setState({ step: 'mapping' })}>
            {i18n.t('← Back to column mapping')}
          </BackLink>
          <Button theme="primary" onClick={this.handleExecute}>
            {i18n.t('Import Now')}
          </Button>
        </Footer>
      </div>
    );
  }

  renderImportingStep() {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Loader />
        <p style={{ marginTop: 16 }}>{i18n.t('Importing records... Please wait.')}</p>
      </div>
    );
  }

  renderCompleteStep() {
    const { csvImport } = this.state;
    if (!csvImport) return null;

    return (
      <div>
        <SuccessBox>
          <SuccessTitle>{i18n.t('Import Complete')}</SuccessTitle>
          <SuccessStats>
            {csvImport.updatedCount > 0
              ? i18n.t(`${csvImport.importedCount} record(s) created, ${csvImport.updatedCount} record(s) updated`)
              : i18n.t(`${csvImport.importedCount} record(s) imported successfully`)
            }
          </SuccessStats>
          {csvImport.skippedCount > 0 && (
            <SuccessStats style={{ color: '#999' }}>
              {i18n.t(`${csvImport.skippedCount} row(s) skipped`)}
            </SuccessStats>
          )}
        </SuccessBox>

        {csvImport.skippedCount > 0 && csvImport.skippedDetails && (
          <div>
            <h4>{i18n.t('Skipped Rows')}</h4>
            <SkippedTable>
              <thead>
                <tr>
                  <th>{i18n.t('Row')}</th>
                  <th>{i18n.t('Reason')}</th>
                </tr>
              </thead>
              <tbody>
                {csvImport.skippedDetails.slice(0, 50).map((s, idx) => (
                  <tr key={idx}>
                    <td>{s.row}</td>
                    <td>{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </SkippedTable>
          </div>
        )}

        <Footer>
          <div />
          <div style={{ display: 'flex', gap: 12 }}>
            <Button theme="border" onClick={this.handleReset}>
              {i18n.t('Import Another File')}
            </Button>
            <Button theme="primary" onClick={() => this.props.history.push('/settings')}>
              {i18n.t('Done')}
            </Button>
          </div>
        </Footer>
      </div>
    );
  }

  renderCurrentStep() {
    const { step } = this.state;
    switch (step) {
      case 'select': return this.renderSelectStep();
      case 'upload': return this.renderUploadStep();
      case 'mapping': return this.renderMappingStep();
      case 'validating': return this.renderValidatingStep();
      case 'validated': return this.renderValidatedStep();
      case 'importing': return this.renderImportingStep();
      case 'complete': return this.renderCompleteStep();
      default: return null;
    }
  }

  renderPreviousImports() {
    const { step } = this.state;
    if (step !== 'select') return null;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query CSVImportSceneHistoryQuery {
            csvImports {
              csvImportId
              entityType
              fileName
              status
              importedCount
              updatedCount
              skippedCount
              totalRows
              createdAt
            }
          }
        `}
        variables={{}}
        render={({ props: qProps }) => {
          if (!qProps || !qProps.csvImports || qProps.csvImports.length === 0) return null;
          return (
            <PreviousImportsList>
              <h3>{i18n.t('Previous Imports')}</h3>
              {qProps.csvImports.map(imp => (
                <ImportRow key={imp.csvImportId}>
                  <div>
                    <strong>{imp.fileName}</strong>
                    <span style={{ color: '#999', marginLeft: 8, fontSize: 13 }}>
                      {imp.entityType} · {imp.totalRows} rows
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {imp.status === 'COMPLETED' && (
                      <span style={{ fontSize: 13, color: '#666' }}>
                        {imp.importedCount} created{imp.updatedCount > 0 ? `, ${imp.updatedCount} updated` : ''}, {imp.skippedCount} skipped
                      </span>
                    )}
                    <Badge status={imp.status}>{imp.status}</Badge>
                  </div>
                </ImportRow>
              ))}
            </PreviousImportsList>
          );
        }}
      />
    );
  }

  render() {
    const { error } = this.state;

    return (
      <Page>
        <PageHeader title={i18n.t('Data Import')} />
        <Container>
          {this.renderStepIndicator()}
          {error && <ErrorBox>{error}</ErrorBox>}
          {this.renderCurrentStep()}
          {this.renderPreviousImports()}
        </Container>
      </Page>
    );
  }
}

export default CSVImportScene;
